/** Phase 5J — SCR-MER-072–090 reviews & analytics bindings (07-02) */

export const REVIEWS_ANALYTICS_SCREEN_IDS = [
  'SCR-MER-072',
  'SCR-MER-073',
  'SCR-MER-074',
  'SCR-MER-075',
  'SCR-MER-076',
  'SCR-MER-077',
  'SCR-MER-078',
  'SCR-MER-079',
  'SCR-MER-080',
  'SCR-MER-081',
  'SCR-MER-082',
  'SCR-MER-083',
  'SCR-MER-084',
  'SCR-MER-085',
  'SCR-MER-086',
  'SCR-MER-087',
  'SCR-MER-088',
  'SCR-MER-089',
  'SCR-MER-090',
] as const;

export type ReviewsAnalyticsScreenId = (typeof REVIEWS_ANALYTICS_SCREEN_IDS)[number];

export const REVIEWS_ANALYTICS_ROUTES: Record<ReviewsAnalyticsScreenId, string> = {
  'SCR-MER-072': '/age-verification',
  'SCR-MER-073': '/review-list',
  'SCR-MER-074': '/respond-to-review',
  'SCR-MER-075': '/report-review',
  'SCR-MER-076': '/trading-dashboard',
  'SCR-MER-077': '/sales-report',
  'SCR-MER-078': '/operations-report',
  'SCR-MER-079': '/product-performance',
  'SCR-MER-080': '/export-centre',
  'SCR-MER-081': '/notification-settings',
  'SCR-MER-082': '/business-settings',
  'SCR-MER-083': '/integrations-overview',
  'SCR-MER-084': '/api-clients',
  'SCR-MER-085': '/webhooks',
  'SCR-MER-086': '/webhook-deliveries',
  'SCR-MER-087': '/notification-drawer',
  'SCR-MER-088': '/weigh-item',
  'SCR-MER-089': '/weight-exceptions',
  'SCR-MER-090': '/returned-order',
};

export const REVIEWS_ANALYTICS_PERMISSIONS: Partial<
  Record<ReviewsAnalyticsScreenId, readonly string[]>
> = {
  'SCR-MER-072': ['PERM-fulfilment.agecheck.record'],
  'SCR-MER-073': ['PERM-review.review.list', 'PERM-review.review.read'],
  'SCR-MER-074': ['PERM-review.response.create'],
  'SCR-MER-075': ['PERM-review.review.report'],
  'SCR-MER-076': ['PERM-report.merchant.read'],
  'SCR-MER-077': ['PERM-report.merchant.read'],
  'SCR-MER-078': ['PERM-report.merchant.read'],
  'SCR-MER-079': ['PERM-report.merchant.read'],
  'SCR-MER-080': ['PERM-report.merchant.export'],
  'SCR-MER-081': ['PERM-tenant.business.update'],
  'SCR-MER-082': ['PERM-tenant.business.update'],
  'SCR-MER-083': ['PERM-identity.apiclient.read', 'PERM-webhook.subscription.read'],
  'SCR-MER-084': [
    'PERM-identity.apiclient.read',
    'PERM-identity.apiclient.create',
    'PERM-identity.apiclient.rotate',
    'PERM-identity.apiclient.revoke',
  ],
  'SCR-MER-085': [
    'PERM-webhook.subscription.read',
    'PERM-webhook.subscription.create',
    'PERM-webhook.subscription.update',
  ],
  'SCR-MER-086': ['PERM-webhook.subscription.read', 'PERM-webhook.subscription.update'],
  'SCR-MER-087': ['PERM-notification.history.read'],
  'SCR-MER-088': ['PERM-fulfilment.pick.execute', 'PERM-fulfilment.job.read'],
  'SCR-MER-089': ['PERM-fulfilment.pick.execute', 'PERM-fulfilment.job.read'],
  'SCR-MER-090': [
    'PERM-fulfilment.handover.read',
    'PERM-inventory.stock.update',
    'PERM-order.replacement.create',
  ],
};

/** Default HTTPS webhook subscription event for create honesty. */
export const DEFAULT_WEBHOOK_EVENT_TYPES = ['order.accepted'] as const;

export const REVIEWS_ANALYTICS_ENDPOINTS: Partial<
  Record<ReviewsAnalyticsScreenId, readonly string[]>
> = {
  'SCR-MER-072': ['POST /merchant/v1/orders/{id}/age-check'],
  'SCR-MER-073': ['GET /merchant/v1/reviews'],
  'SCR-MER-074': [
    'GET /merchant/v1/reviews/{id}',
    'POST /merchant/v1/reviews/{id}/response',
    'PATCH /merchant/v1/reviews/{id}/response',
  ],
  'SCR-MER-075': ['GET /merchant/v1/reviews/{id}', 'POST /merchant/v1/reviews/{id}/report'],
  'SCR-MER-076': ['GET /merchant/v1/reports/{reportType}', 'GET /merchant/v1/dashboard/trading'],
  'SCR-MER-077': ['GET /merchant/v1/reports/{reportType}', 'POST /merchant/v1/reports/exports'],
  'SCR-MER-078': ['GET /merchant/v1/reports/{reportType}', 'POST /merchant/v1/reports/exports'],
  'SCR-MER-079': ['GET /merchant/v1/reports/{reportType}', 'POST /merchant/v1/reports/exports'],
  'SCR-MER-080': [
    'GET /merchant/v1/reports/exports',
    'POST /merchant/v1/reports/exports',
    'GET /merchant/v1/reports/runs/{runId}',
    'GET /merchant/v1/reports/runs/{runId}/output',
  ],
  'SCR-MER-081': ['GET /merchant/v1/settings', 'PATCH /merchant/v1/settings'],
  'SCR-MER-082': [
    'GET /merchant/v1/business',
    'PATCH /merchant/v1/business',
    'GET /merchant/v1/business/status',
    'GET /merchant/v1/business/commercial-terms',
    'GET /merchant/v1/business/commercial-terms/history',
    'GET /merchant/v1/business/vat-registration',
  ],
  'SCR-MER-083': ['GET /merchant/v1/settings/api-keys', 'GET /merchant/v1/webhooks'],
  'SCR-MER-084': [
    'GET /merchant/v1/settings/api-keys',
    'POST /merchant/v1/settings/api-keys',
    'POST /merchant/v1/settings/api-keys/{id}/rotate',
    'DELETE /merchant/v1/settings/api-keys/{id}',
  ],
  'SCR-MER-085': [
    'GET /merchant/v1/webhooks',
    'POST /merchant/v1/webhooks',
    'PATCH /merchant/v1/webhooks/{id}',
  ],
  'SCR-MER-086': [
    'GET /merchant/v1/webhooks',
    'GET /merchant/v1/webhooks/{id}',
    'POST /merchant/v1/webhooks/{id}/test',
  ],
  'SCR-MER-087': [
    'GET /merchant/v1/notifications',
    'POST /merchant/v1/notifications/{id}/read',
    'POST /merchant/v1/notifications/read-all',
  ],
  'SCR-MER-088': [
    'GET /merchant/v1/orders/{id}/pick-list',
    'POST /merchant/v1/orders/{id}/pick-list/items/{itemId}/weight',
  ],
  'SCR-MER-089': ['GET /merchant/v1/orders/{id}/pick-list'],
  'SCR-MER-090': [
    'GET /merchant/v1/orders/{id}',
    'GET /merchant/v1/orders/{id}/replacement',
    'POST /merchant/v1/orders/{id}/replacement',
    'PATCH /merchant/v1/branches/{id}/inventory/{itemId}',
  ],
};

export const REVIEW_ID_STORAGE_KEY = 'beatq.merchant.reviewId';
export const WEBHOOK_ID_STORAGE_KEY = 'beatq.merchant.webhookId';
export const REPORT_RUN_ID_STORAGE_KEY = 'beatq.merchant.reportRunId';
export const REPORT_RUNS_STORAGE_KEY = 'beatq.merchant.reportRuns';

/** ○ poll interval for SCR-MER-073 (07-02 realtime marker). */
export const REVIEWS_POLL_INTERVAL_MS = 30_000;
/** ○ poll interval for SCR-MER-076 / SCR-MER-080 (07-02 realtime marker). */
export const REPORT_POLL_INTERVAL_MS = 30_000;
export const REPORT_RUN_POLL_INTERVAL_MS = 5_000;

/** 10-05b `GET /reports/{reportType}` slugs (backend aliases map to RPT-MER-*). */
export const MERCHANT_REPORT_TYPES = {
  salesSummary: 'sales-summary',
  salesByProduct: 'sales-by-product',
  salesByCategory: 'sales-by-category',
  fulfilment: 'fulfilment-performance',
  cancellations: 'cancellations-and-rejections',
  reviewsSummary: 'reviews-summary',
  product: 'sales-by-product',
  trading: 'sales-summary',
  sales: 'sales-summary',
  operations: 'fulfilment-performance',
} as const;

export const MERCHANT_EXPORT_REPORT_OPTIONS = [
  { value: 'sales-summary', label: 'Sales summary' },
  { value: 'sales-by-category', label: 'Sales by category' },
  { value: 'sales-by-product', label: 'Sales by product' },
  { value: 'fulfilment-performance', label: 'Operations' },
  { value: 'cancellations-and-rejections', label: 'Cancellations and rejections' },
  { value: 'reviews-summary', label: 'Reviews summary' },
] as const;

/**
 * 07-02 actors for report pages (NAV-6 omit is M01; pages still gate deep links).
 * 076/077 BO+BM+FV; 078/079 BO+BM not FV; 080 BO+FV not BM.
 */
export const REPORT_SCREEN_ROLES: Partial<Record<ReviewsAnalyticsScreenId, readonly string[]>> = {
  'SCR-MER-076': ['ROLE-MER-001', 'ROLE-MER-002', 'ROLE-MER-007'],
  'SCR-MER-077': ['ROLE-MER-001', 'ROLE-MER-002', 'ROLE-MER-007'],
  'SCR-MER-078': ['ROLE-MER-001', 'ROLE-MER-002'],
  'SCR-MER-079': ['ROLE-MER-001', 'ROLE-MER-002'],
  'SCR-MER-080': ['ROLE-MER-001', 'ROLE-MER-007'],
};

export function reportScreenAllowsRoles(
  screenId: ReviewsAnalyticsScreenId,
  roles: readonly string[],
): boolean {
  const allowed = REPORT_SCREEN_ROLES[screenId];
  if (!allowed) return false;
  if (roles.length === 0) return true;
  return roles.some((role) => allowed.includes(role));
}

/** 10-05b POST /reviews/{id}/report reason catalogue. */
export const REVIEW_REPORT_REASONS = [
  'ABUSIVE',
  'PERSONAL_DATA',
  'FACTUALLY_INCORRECT',
  'NOT_ABOUT_THIS_BUSINESS',
  'SPAM',
  'COMPETITOR',
  'EXTORTION_ATTEMPT',
] as const;

export const AGE_CHECK_OUTCOMES = ['PASSED', 'FAILED', 'CHALLENGED'] as const;
